import { fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { matchesStaffSession, type StaffSessionState } from "@/store/staffSessionSlice"
import { captureStaffEpoch, assertStaffWriteAvailable, StaffWriteUnavailableError } from "./staffTransportSession"

export type StaffRequest = FetchArgs & { staffSession?: StaffSessionState }
export interface StaffTransportMeta { status?: number }

/** Endpoint policy, never accepted from a notification or query argument. */
export interface StaffEndpointPolicy {
  workflowProtocol?: boolean
}

export interface StaffTransportOptions {
  baseUrl: string
  getAccessToken: () => Promise<string>
  fetchFn?: typeof fetch
}

/** Shared staff transport. Deliberately no retry wrapper, including on 401 or network loss. */
export function createStaffBaseQuery({ baseUrl, getAccessToken, fetchFn }: StaffTransportOptions): BaseQueryFn<
  string | StaffRequest, unknown, FetchBaseQueryError, StaffEndpointPolicy, StaffTransportMeta
> {
  const fetchQuery = fetchBaseQuery({ baseUrl, fetchFn })
  return async (args, api, policy) => {
    const { staffSession, ...request } = typeof args === "string" ? { url: args } : args
    const epoch = captureStaffEpoch()
    const scopeIsCurrent = () => epoch === captureStaffEpoch() && (!staffSession || matchesStaffSession(api.getState(), staffSession))
    const scopeError = (beforeSend: boolean) => ({ error: {
      status: "CUSTOM_ERROR" as const, error: "Staff session changed.",
      data: { code: beforeSend ? "scope-unavailable-before-send" : "scope-expired" },
    } })
    const writeAvailable = () => {
      if (!["GET", "HEAD"].includes((request.method ?? "GET").toUpperCase())) assertStaffWriteAvailable(request.url)
    }
    const accessError = (error: unknown) => ({ error: { status: "CUSTOM_ERROR" as const,
      error: "Saving is temporarily unavailable; the request was not sent.",
      data: { code: error instanceof StaffWriteUnavailableError ? error.code : "access-check-unavailable" } } })
    if (!scopeIsCurrent()) return scopeError(true)
    try { writeAvailable() } catch (error) { return accessError(error) }
    let token: string
    try {
      token = await getAccessToken()
    } catch {
      // No request was sent. Never put the auth exception/token into Redux or DevTools.
      return { error: { status: "CUSTOM_ERROR", error: "Authentication unavailable.", data: { code: "authentication-unavailable" } } }
    }
    if (api.signal.aborted) {
      return { error: { status: "CUSTOM_ERROR", error: "Request cancelled before send.", data: { code: "cancelled-before-send" } } }
    }
    if (!scopeIsCurrent()) return scopeError(true)
    try { writeAvailable() } catch (error) { return accessError(error) }
    const headerEntries = request.headers instanceof Headers || Array.isArray(request.headers)
      ? request.headers
      : Object.entries(request.headers ?? {}).filter(([, value]) => value !== undefined)
    const headers = new Headers(headerEntries as HeadersInit)
    headers.set("Authorization", `Bearer ${token}`)
    headers.set("Accept", "application/json")
    if (policy?.workflowProtocol) headers.set("X-CMS-Workflow-Protocol", "2")
    const result = await fetchQuery({ ...request, headers }, api, {})
    if (!scopeIsCurrent()) return scopeError(false)
    // Request/Response objects (including bearer headers) must not enter Redux actions.
    // Only serializable status metadata crosses the transport boundary.
    const meta = { status: result.meta?.response?.status }
    if (result.error) return { error: result.error, meta }
    // Match lib/api's write 204 contract. JSON and error bodies remain intact.
    return { data: meta.status === 204 ? undefined : result.data, meta }
  }
}
